import React, { useState } from 'react';

import { IconScout } from '@/components/ui/images/IconScout';
import { Text } from '@/components/ui/text/Text';
import { Divider } from '@/components/ui/separators';
import { colors } from '@/assets/styles/colors';


export type CollapsibleSectionVariant = 'plain' | 'filled';

export interface CollapsibleSectionProps {
  title: string;
  count?: number;
  defaultExpanded?: boolean;
  /**
   * `plain` (por defecto): separador superior, título y contador concatenados, sin fondo.
   * `filled`: encabezado sobre una barra de fondo, con el contador alineado a la derecha
   * junto al chevron y sin separador.
   */
  variant?: CollapsibleSectionVariant;
  children: React.ReactNode;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  count,
  defaultExpanded = true,
  variant = 'plain',
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const isFilled = variant === 'filled';
  const hasCount = count !== undefined;
  const headerLabel = !isFilled && hasCount ? `${title} (${count})` : title;

  return (
    <div>
      {!isFilled && <Divider />}
      <button
        type="button"
        className={
          isFilled
            ? 'flex items-center justify-between w-full gap-2 px-3 py-2 rounded'
            : 'flex items-center justify-between w-full py-3'
        }
        style={isFilled ? { backgroundColor: colors.primary.background } : undefined}
        onClick={() => setIsExpanded((previous) => !previous)}
        aria-expanded={isExpanded}
      >
        <Text
          variant={isFilled ? 'subheader-medium' : 'body-medium'}
          color={isFilled ? colors.primary.main : colors.primary.dash}
        >
          {headerLabel}
        </Text>
        <span className="flex items-center gap-2">
          {isFilled && hasCount && (
            <Text variant="small-regular" color={colors.grays.neutral66}>
              {`(${count})`}
            </Text>
          )}
          <IconScout
            name="angleDown"
            size="md"
            color={isFilled ? colors.important.main : colors.primary.dash}
            className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {isExpanded && children}
    </div>
  );
};

export default CollapsibleSection;
